import { Component, Inject, LOCALE_ID, OnDestroy } from '@angular/core';
import { formatNumber } from '@angular/common';
import { Observable, Subscription, Subject, combineLatest } from 'rxjs';
import { withLatestFrom, distinctUntilChanged } from 'rxjs/operators';
import {
  IDatasource,
  GridReadyEvent,
  GridOptions,
  ValueFormatterParams
} from 'ag-grid-community';
import {
  InfiniteScrollingService,
  stringComparer,
  numberComparer,
  ComparatorMap,
  ReusableStreamProperty
} from '@ipreo/muni-shared';
import { AccountCellRendererComponent } from '../../../../shared/components/renderers/account-cell.component';
import { AccountsReportsService } from '../../../services/accounts.service';
import { NegativeCasesService } from '../../../../gameday/services/negative-cases.service';
import { NegativeMessages } from '../../../../state/negative-cases/state';
import { AccountReport } from '../../../models/account';
import { ColumnDefinition } from '../../../../models/column';

@Component({
  providers: [AccountsReportsService],
  selector: 'muni-gameday-accounts-reports',
  templateUrl: './accounts.template.html',
  styleUrls: ['./accounts.styles.scss'],
  standalone: false
})
export class AccountsReportsComponent implements OnDestroy {
  private readonly gridReadySubj = new Subject<GridReadyEvent>();
  public readonly columnDefs: ColumnDefinition[] = [
    {
      headerName: 'Account',
      field: 'name',
      cellRenderer: AccountCellRendererComponent,
      suppressHeaderMenuButton: true,
      comparator: stringComparer
    },
    {
      headerName: 'Retail',
      field: 'retailAmount',
      type: 'numericColumn',
      suppressHeaderMenuButton: true,
      valueFormatter: ({ value }: ValueFormatterParams) =>
        formatNumber(value / 1000 || 0, this.locale),
      comparator: numberComparer
    },
    {
      headerName: 'Institutional',
      field: 'institutionalAmount',
      type: 'numericColumn',
      suppressHeaderMenuButton: true,
      valueFormatter: ({ value }: ValueFormatterParams) =>
        formatNumber(value / 1000 || 0, this.locale),
      comparator: numberComparer
    },
    {
      headerName: 'Total',
      field: 'total',
      type: 'numericColumn',
      suppressHeaderMenuButton: true,
      valueFormatter: ({ value }: ValueFormatterParams) =>
        formatNumber(value / 1000 || 0, this.locale),
      comparator: numberComparer,
      sort: 'desc'
    },
    {
      headerName: 'Money Type',
      field: 'moneyType',
      type: 'numericColumn', // for right align
      suppressHeaderMenuButton: true,
      comparator: stringComparer
    }
  ];

  public readonly gridOptions: GridOptions = {
    onGridReady: (params: GridReadyEvent) => {
      this.gridReadySubj.next(params);
    },
    autoSizeStrategy: { type: 'fitGridWidth' },
    onGridSizeChanged: ({ api }) => api.sizeColumnsToFit()
  };

  public readonly totals: Observable<AccountReport> =
    this.accountsReportsService.getAccountsTotalsByTypes();

  public readonly source: Observable<AccountReport[]> =
    this.accountsReportsService.getAccountAmountsData();

  @ReusableStreamProperty
  public readonly emptyMessage: Observable<string> =
    this.negativeCasesService.messages.pipe(
      withLatestFrom(this.totals, (messages, totals) =>
        !totals.total && !messages.accounts
          ? NegativeMessages.NoFilteredDealOrders
          : messages.accounts
      ),
      distinctUntilChanged()
    );

  @ReusableStreamProperty
  private readonly grid$: Observable<
    [GridReadyEvent, AccountReport[], AccountReport]
  > = combineLatest(this.gridReadySubj, this.source, this.totals);

  private readonly gridSubscription: Subscription = this.grid$.subscribe(
    ([{ api }, account, totals]) => {
      const sortMap: ComparatorMap[] = this.columnDefs
        .map(({ field, comparator }: ColumnDefinition) => ({
          colId: field,
          comparator: comparator
            ? (a: any, b: any) => (comparator as any)(a, b)
            : undefined
        }))
        .filter((item) => item.comparator);
      const datasource: IDatasource =
        this.infiniteScrollingService.getDataSource(
          account,
          undefined,
          sortMap
        );
      api.setDatasource(datasource);
      api.setGridOption('pinnedTopRowData', [totals]);
    }
  );

  constructor(
    private accountsReportsService: AccountsReportsService,
    private negativeCasesService: NegativeCasesService,
    private infiniteScrollingService: InfiniteScrollingService,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  public ngOnDestroy(): void {
    this.gridSubscription.unsubscribe();
  }
}
